// Angular imports
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

// CC-Common imports
import { IconConstants } from 'cc-common';

// Constant imports
import { Constants } from '../../../_constants/constants';
import { StringFormatConstants } from '../../../_constants/string-format-constants';
import { AccConstants } from '../../_constants/acc-constant';

// Service imports
import { HelperService } from '../../../_services/shared-services/helper.service';
import { ServerErrorService } from '../../../_services/shared-services/server-error.service';
import { SharedAccApplicationService } from '../../_services/shared-acc-application.service';

// Model imports
import { AccBaseParameterDto } from '../../_models/acc-base-parameter-dto';
import { AccDetailResultDto } from '../../_models/acc-detail-result-dto';
import { AccDetailsParameterDto } from '../../_models/acc-details-parameter-dto';
import { AccDocumentDetailParametersDto } from '../../_models/acc-document-detail-parameters-dto';
import { AccHistoryResultDto } from '../../_models/acc-history-result-dto';
import { AccProjectDetailResultDto } from '../../_models/acc-project-detail-result-dto';

@Component({
    selector: 'app-architectural-application-viewer',
    templateUrl: './architectural-application-viewer.component.html',
    styleUrls: ['./architectural-application-viewer.component.scss']
})
export class ArchitecturalApplicationViewerComponent implements OnInit {
    @Input() visible = false;
    @Input() popupTitle: string;
    @Input() accDetailParam: any;
    @Input() isInternalUser: boolean;
    @Input() isClosedApplication = false;
    @Output() closeApplicationEditorPopup = new EventEmitter<boolean>();

    loadingVisible = false;
    defaultLabelLocation = Constants.defaultFormLabelLocation;
    dateFormat = StringFormatConstants.dateFormat;

    boardApprovalButtonOptions: any;
    historyButtonOptions: any;
    attachmentButtonOptions: any;

    iconClose = IconConstants.CLOSE;
    iconSave = IconConstants.SAVE;
    iconCheck = IconConstants.CHECK;
    iconLog = IconConstants.HISTORY;
    iconAttach = IconConstants.ATTACHMENT;

    itemNotes = AccConstants.ITEM_NOTES;
    projectNotes = AccConstants.PROJECT_NOTES;
    lineItemLog = AccConstants.ITEM_LOGS;
    notesType: string;

    transactionLogTitle: string;

    showBoardApprovalPopup = false;
    showTransactionLog = false;
    showNotesEditor = false;
    showAttachments = false;

    notesEditorParam: AccBaseParameterDto = new AccBaseParameterDto();
    docParam: AccDocumentDetailParametersDto = new AccDocumentDetailParametersDto();
    projectData: AccProjectDetailResultDto = new AccProjectDetailResultDto();
    transactionLogResultData: AccHistoryResultDto[] = [];

    constructor(
        private readonly errorService: ServerErrorService,
        private readonly accApplicationService: SharedAccApplicationService,
        private readonly helperService: HelperService
    ) {
        this.onBoardApprovalClick = this.onBoardApprovalClick.bind(this);
        this.onTransactionLogClick = this.onTransactionLogClick.bind(this);
        this.onProjectDocAttachmentClick = this.onProjectDocAttachmentClick.bind(this);
    }

    ngOnInit() {
        this.projectData.items = [];
        if (!!this.accDetailParam) {
            this.populateAccDetail();
        }

        this.boardApprovalButtonOptions = {
            icon: this.iconCheck,
            hint: 'Board Approval',
            type: 'success',
            onClick: this.onBoardApprovalClick
        };

        this.historyButtonOptions = {
            icon: this.iconLog,
            hint: 'Internal Project Log',
            onClick: this.onTransactionLogClick
        };

        this.attachmentButtonOptions = {
            icon: this.iconAttach,
            type: 'default',
            hint: 'Project Documents',
            onClick: this.onProjectDocAttachmentClick
        };
    }

    onClosePopup(): void {
        this.visible = false;
        this.closeApplicationEditorPopup.emit(false);
    }

    onBoardApprovalClick(): void {
        this.showBoardApprovalPopup = true;
    }

    onProjectNotesClick(): void {
        this.notesEditorParam.communityId = this.accDetailParam.communityId;
        this.notesEditorParam.customerId = this.accDetailParam.customerId;
        this.notesEditorParam.accItemId = this.accDetailParam.accItemId;
        this.notesEditorParam.clearCache = true;
        this.notesType = this.projectNotes;
        this.showNotesEditor = true;
    }

    onItemNotesClick(data: any): void {
        this.notesEditorParam.communityId = this.accDetailParam.communityId;
        this.notesEditorParam.customerId = this.accDetailParam.customerId;
        this.notesEditorParam.accItemId = data.accItemId;
        this.notesEditorParam.clearCache = true;
        this.notesType = this.itemNotes;
        this.showNotesEditor = true;
    }

    onProjectDocAttachmentClick(): void {
        this.docParam.communityId = this.accDetailParam.communityId;
        this.docParam.customerId = this.accDetailParam.customerId;
        this.docParam.accItemId = this.accDetailParam.accItemId;
        this.showAttachments = true;
    }

    onLineItemAttachmentClick(data: any): void {
        if (!!data) {
            this.docParam.communityId = this.accDetailParam.communityId;
            this.docParam.customerId = this.accDetailParam.customerId;
            this.docParam.accItemId = data.accItemId;
            this.showAttachments = true;
        }
    }

    onAttachmentClose(): void {
        this.showAttachments = false;
    }

    onTransactionLogClick(data: any, logType: string): void {
        const param: AccBaseParameterDto = new AccBaseParameterDto();
        param.communityId = this.accDetailParam.communityId;
        param.customerId = this.accDetailParam.customerId;
        param.clearCache = true;
        let transactionLogService: any;
        if (logType === this.lineItemLog) {
            param.accItemId = data.accItemId;
            this.transactionLogTitle = `Log of ${data.accItemId}`;
            transactionLogService = this.accApplicationService.getAccItemHistoryLog(param);
        } else {
            param.accItemId = this.accDetailParam.accItemId;
            this.transactionLogTitle = `Architectural Request Approval Log: ${this.accDetailParam.accItemId}`;
            transactionLogService = this.accApplicationService.getAccProjectHistoryLog(param);
        }
        this.loadingVisible = true;
        transactionLogService.subscribe(
            (result: AccHistoryResultDto[]) => {
                this.transactionLogResultData = result;
                this.showTransactionLog = true;
                this.loadingVisible = false;
            },
            (error) => {
                this.loadingVisible = false;
                this.errorService.displayError(error);
            }
        );
    }

    private updateOwner(): void {
        const dialog = this.helperService.getConfirmationDialog(
            'Warning: Outdated Owner',
            'The selected owner for this ACC is no longer the current owner. <br/> Please update ACC information',
            'Update',
            'Cancel',
            'success',
            'default'
        );
        dialog.show().then((answer) => {
            if (answer) {
                this.projectData.isCurrentOwner = true;
            }
        });
    }

    private populateAccDetail(): void {
        const param: AccDetailsParameterDto = new AccDetailsParameterDto();
        param.accItemId = this.accDetailParam.accItemId;
        param.communityId = this.accDetailParam.communityId;
        param.customerId = this.accDetailParam.customerId;
        this.loadingVisible = true;
        this.accApplicationService.getAccDetails(param).subscribe(
            (result: AccDetailResultDto) => {
                this.projectData = result.projectData;
                if (!this.projectData.isCurrentOwner) {
                    this.updateOwner();
                }
                this.loadingVisible = false;
            },
            (error) => {
                this.loadingVisible = false;
                this.errorService.displayError(error);
            }
        );
    }
}
